<template>
    <div class="text-center">
        <div class="m-b-5" v-if="!session.is_completed">
            <a class="btn btn-info" type="button" @click="requestLocation()">
                {{ $t("virtual_visit:request_location") }}
                <i v-if="status == 'in_progress'" class="fa fa-spinner fa-spin"></i>
            </a>

            <a v-if="!client_location && client_location_requested" class="btn btn-info m-l-10" type="button" @click="$emit('finished', step_no, true)">
                {{ $t("virtual_visit:location:continue_without") }}
            </a>
        </div>

        <div v-if="msg">{{ msg }}</div>
        <div v-if="status == 'location_request_denied'">
            <ModeratorEnableLocation />
        </div>

        <!-- The client may be standing at a different property than the one
             being appraised - warn, never block (#3974) -->
        <p v-if="session.client_location_looks_wrong" class="text-warning m-t-10 m-b-10">
            <i class="fa fa-exclamation-triangle m-r-5"></i>
            {{ $t("virtual_visit:location:distance_from_property") }}
            <strong>{{ distanceFromProperty }}</strong>
        </p>

        <div class="vld-parent">
            <loading :active="status == 'in_progress'" :can-cancel="false" :is-full-page="false" vl/>

            <!-- fit-bounds-max-zoom: with a single marker fitBounds ignores
                 :zoom and would jump to max-zoom; OSM tiles end at z19, so
                 max-zoom stays within tile coverage -->
            <k-simple-leaflet-map
                v-if="client_location"
                :fullscreen="true"
                :zoom="15"
                :max-zoom="19"
                :fit-bounds-max-zoom="15"
                :center="client_location"
                :markers="markers"
                :height="200"
            >
            </k-simple-leaflet-map>
        </div>
    </div>
</template>

<script>
import KSimpleLeafletMap from "@/components/k-leaflet-map/KSimpleLeafletMap";
import mixins from "../mixins.js"
import ModeratorEnableLocation from "../instructions/ModeratorEnableLocation.vue";

export default {
    mixins: [ mixins ],
    model: {
        prop: 'session',
        event: 'change'
    },
    props: {
        session: {
            type: Object,
            required: true,
        },
    },
    components: {
        "k-simple-leaflet-map": KSimpleLeafletMap,
        ModeratorEnableLocation,
    },
    data: function () {
        return {
            step_no: 2,

            // A shade longer than the client's own geolocation timeout, so its
            // reply wins the race whenever it does arrive (#4016)
            LOCATION_TIMEOUT_MS: 25000,
            locationTimeout: null,

            client_location_requested: false,
            status: null,
            // in_progress
            // failed
            // success
            // location_request_denied
        };
    },
    methods: {
        requestLocation: function () {
            const app = this;
            app.client_location_requested = true;
            app.clearLocationTimeout();
            app.$store.state.OTsession.signal(
                {
                    type: "location",
                    data: "location_request",
                },
                (error) => {
                    if (error) {
                        app.status = "failed";
                    } else {
                        app.status = "in_progress";
                        // The client answers within its own 20s geolocation
                        // timeout; past that its reply is lost, so recover here
                        // rather than spin for good (#4016)
                        app.locationTimeout = setTimeout(() => {
                            if (app.status === "in_progress") {
                                app.status = "failed";
                                app.notify(
                                    app.$t("virtual_visit:location_request:error_message"), "", "error");
                            }
                        }, app.LOCATION_TIMEOUT_MS);
                    }
                }
            );
        },
        handleLocationReported: function (event) {
            const app = this;
            app.clearLocationTimeout();

            if (event.data === "ERROR_REJECTED") {
                app.notify(
                    this.$t("virtual_visit:location:client_rejected"),
                    "",
                    "error"
                );
                app.status = "location_request_denied";
                return;
            }

            if (event.data === "location_saved") {
                // The client saved its GPS position directly to our backend
                // (#3867) - refetch the session to get client_gps
                app.status = "success";
                app.$emit("reloadSessionData");
                app.$emit("finished", app.step_no);
                app.notify(this.$t("virtual_visit:location:client_received"), "", "success");
                return;
            }

            // Anything else is one of the client's ERROR_* results. They used to
            // fall through here, leaving the step spinning for good (#4016).
            app.status = "failed";
            app.notify(this.$t("virtual_visit:location_request:error_message"), "", "error");
        },
        clearLocationTimeout: function () {
            if (this.locationTimeout) {
                clearTimeout(this.locationTimeout);
                this.locationTimeout = null;
            }
        },
        setupOTSessionListeners: function () {
            const app = this;
            if (!app.OTsession) { return; }
            app.OTsession.on("signal", (event) => {
                // OpenTok also delivers the request back to its sender, and that is no reply
                if (event.type == "signal:location" && event.data !== "location_request") {
                    app.handleLocationReported(event);
                }
            });
        },
    },
    computed: {
        OTsession() {
            return this.$store.state.OTsession;
        },
        markers() {
            return this.client_location
                ? [{ coordinates: this.client_location }]
                : null;
        },
        msg() {
            let msg;
            if (this.status == "in_progress") {
                msg = this.$t("virtual_visit:location_request:in_progress");
            } else if (this.status == "failed") {
                msg = this.$t("virtual_visit:location_request:error_message");
            }
            return msg;
        },
        client_location() {
            return this.session.client_gps
                ? {
                    lat: parseFloat(this.session.client_gps.coordinates[1]),
                    lng: parseFloat(this.session.client_gps.coordinates[0]),
                }
                : null;
        },
        distanceFromProperty() {
            const meters = this.session.client_location_distance_m;
            if (!meters) { return ""; }
            return meters >= 1000
                ? `${(meters / 1000).toFixed(1)} km`
                : `${Math.round(meters)} m`;
        },
    },
    created() {
        if (this.session.client_gps) {
            this.$emit("finished", this.step_no);
        }
    },
    beforeDestroy() {
        this.clearLocationTimeout();
    },
    watch: {
        OTsession: {
            immediate: true,
            handler: function (newVal, oldVal) {
                if (newVal && !oldVal) {
                    this.setupOTSessionListeners();
                }
            },
        },
    },
};
</script>
